// src/pages/processifyd/ProcessifydReferences.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const ProcessifydReferences = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [references, setReferences] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingReference, setEditingReference] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    referenceDocTitle: "",
    aboutTheReferenceDoc: "",
    referenceDocIsUsed: "",
    stateName: "",
    processName: "",
    referenceDocType: "",
    referenceDocFileType: "",
    referenceDocFileName: "",
    referenceDocFileLocation: "",
    referenceDocOwner: "",
    active: "Yes",
  };

  const [formData, setFormData] = useState(initialForm);

  // Sample Demo Data
  const demoReferences = [
    {
      id: 1,
      referenceDocTitle: "User Training Manual",
      aboutTheReferenceDoc: "User Training Manual",
      referenceDocIsUsed: "Common",
      stateName: "None",
      processName: "None",
      referenceDocType: "Text",
      referenceDocFileType: "PDF",
      referenceDocFileName: "",
      referenceDocFileLocation: "",
      referenceDocOwner: "Business Team",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "12/12/23 15:32",
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: "12/12/23 15:32",
    },
    {
      id: 2,
      referenceDocTitle: "Loan Approval Policy 2024",
      aboutTheReferenceDoc: "Official policy document for loan approval workflow",
      referenceDocIsUsed: "Loan Processing",
      stateName: "None",
      processName: "Loan Disbursement Process",
      referenceDocType: "Policy",
      referenceDocFileType: "PDF",
      referenceDocFileName: "Loan_Policy_v2.pdf",
      referenceDocFileLocation: "/documents/policies/",
      referenceDocOwner: "Compliance Team",
      active: "Yes",
      createdBy: "Sanjeev Kumar",
      createdDate: "05/02/24 09:20",
      lastUpdatedBy: "Priya Sharma",
      lastUpdateDate: "12/03/24 11:45",
    },
  ];

  const loadSample = () => {
    setReferences(demoReferences);
  };

  const handleAddNew = () => {
    setEditingReference(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (ref) => {
    setEditingReference(ref);
    setFormData({
      referenceDocTitle: ref.referenceDocTitle,
      aboutTheReferenceDoc: ref.aboutTheReferenceDoc || "",
      referenceDocIsUsed: ref.referenceDocIsUsed || "",
      stateName: ref.stateName || "",
      processName: ref.processName || "",
      referenceDocType: ref.referenceDocType || "",
      referenceDocFileType: ref.referenceDocFileType || "",
      referenceDocFileName: ref.referenceDocFileName || "",
      referenceDocFileLocation: ref.referenceDocFileLocation || "",
      referenceDocOwner: ref.referenceDocOwner || "",
      active: ref.active || "Yes",
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this reference document? This action cannot be undone.")) return;
    setReferences((prev) => prev.filter((r) => r.id !== id));
  };

  const handleOpenReference = (ref) => {
    // Update this route as per your actual reference detail page
    navigate(`/processifyd-referencehub/${ref.id}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.referenceDocTitle.trim()) {
      alert("Reference Doc Title is required.");
      return;
    }

    const newReference = {
      id: editingReference?.id || Date.now(),
      ...formData,
      createdBy: editingReference?.createdBy || "Sanjeev Kumar",
      createdDate: editingReference?.createdDate || new Date().toLocaleString("en-IN"),
      lastUpdatedBy: "Sanjeev Kumar",
      lastUpdateDate: new Date().toLocaleString("en-IN"),
    };

    if (editingReference) {
      setReferences((prev) =>
        prev.map((r) => (r.id === editingReference.id ? newReference : r))
      );
    } else {
      setReferences((prev) => [...prev, newReference]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // Filter + Pagination
  const filtered = references.filter(
    (r) =>
      r.referenceDocTitle.toLowerCase().includes(search.toLowerCase()) ||
      r.aboutTheReferenceDoc.toLowerCase().includes(search.toLowerCase()) ||
      r.referenceDocOwner.toLowerCase().includes(search.toLowerCase()) ||
      (r.processName && r.processName.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-6">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            📚 Processifyd
          </div>
          <div className="text-xs text-gray-400">
            Reference Documents Master
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 hover:bg-teal-900/30 rounded-md"
            >
              🔐
            </button>
          )}
        </div>

        <button
          className="md:hidden text-2xl text-cyan-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-4">
          {isLoggedIn && (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="w-12 px-2 py-1 bg-red-700 rounded-md">⏻</button>
            </div>
          )}
        </div>
      )}

      <div className="min-h-[75vh]">
        {/* Controls */}
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex flex-row gap-3">
            <button
              onClick={loadSample}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm px-6 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              Load Sample References
            </button>
            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-2 text-xs lg:text-sm px-6 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Reference Doc
            </button>
          </div>

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by title, owner or process..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-cyan-500/60"
            />
          </div>
        </div>

        {/* Reference Cards */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
          {paginated.map((ref) => (
            <div
              key={ref.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-cyan-600/50 transition-colors group"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-white group-hover:text-cyan-300 transition-colors line-clamp-2">
                    {ref.referenceDocTitle}
                  </h3>
                  <div className="text-sm text-gray-400 mt-1">
                    {ref.referenceDocType} • {ref.referenceDocFileType}
                  </div>
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-medium ${ref.active === "Yes" ? "bg-emerald-600" : "bg-red-600"}`}>
                  {ref.active === "Yes" ? "Active" : "Inactive"}
                </span>
              </div>

              <p className="mt-4 text-sm text-gray-300 line-clamp-3">
                {ref.aboutTheReferenceDoc || "No description available"}
              </p>

              <div className="mt-4 text-xs text-gray-500 space-y-1">
                {ref.referenceDocOwner && <div><strong>Owner:</strong> {ref.referenceDocOwner}</div>}
                {ref.referenceDocIsUsed && <div><strong>Used In:</strong> {ref.referenceDocIsUsed}</div>}
                {ref.processName && ref.processName !== "None" && (
                  <div><strong>Process:</strong> {ref.processName}</div>
                )}
              </div>

              <div className="mt-6 flex gap-2">
                <button
                  onClick={() => handleOpenReference(ref)}
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-700 to-teal-700 hover:from-cyan-600 hover:to-teal-600 rounded-lg font-medium flex items-center justify-center gap-2 text-sm"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  Open
                </button>

                <button
                  onClick={() => handleEdit(ref)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Edit Reference"
                >
                  <FontAwesomeIcon icon={faEdit} className="text-yellow-400" />
                </button>

                <button
                  onClick={() => handleDelete(ref.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  title="Delete Reference"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-red-400" />
                </button>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-500">
              {search ? "No references match your search" : "No reference documents yet. Click 'New Reference Doc' or 'Load Sample References'"}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page <strong>{currentPage}</strong> of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="w-10 px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {/* Add / Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl border border-gray-700 max-h-[92vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-cyan-300">
                  {editingReference ? "Edit Reference Document" : "New Reference Document"}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm mb-1.5">Reference Doc Title <span className="text-red-400">*</span></label>
                  <input
                    required
                    value={formData.referenceDocTitle}
                    onChange={(e) => setFormData({ ...formData, referenceDocTitle: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="e.g. User Training Manual"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1.5">About the Reference Doc</label>
                  <textarea
                    value={formData.aboutTheReferenceDoc}
                    onChange={(e) => setFormData({ ...formData, aboutTheReferenceDoc: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    placeholder="Short description of the document..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Reference Doc is Used</label>
                    <input
                      value={formData.referenceDocIsUsed}
                      onChange={(e) => setFormData({ ...formData, referenceDocIsUsed: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. Common, Loan Processing, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Reference Doc Owner</label>
                    <input
                      value={formData.referenceDocOwner}
                      onChange={(e) => setFormData({ ...formData, referenceDocOwner: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. Business Team"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">State Name</label>
                    <input
                      value={formData.stateName}
                      onChange={(e) => setFormData({ ...formData, stateName: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. None or specific state"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Process Name</label>
                    <input
                      value={formData.processName}
                      onChange={(e) => setFormData({ ...formData, processName: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. None or linked process"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Reference Doc Type</label>
                    <input
                      value={formData.referenceDocType}
                      onChange={(e) => setFormData({ ...formData, referenceDocType: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. Text, Policy, Template"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Reference Doc File Type</label>
                    <input
                      value={formData.referenceDocFileType}
                      onChange={(e) => setFormData({ ...formData, referenceDocFileType: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. PDF, DOCX, XLSX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Active <span className="text-red-400">*</span></label>
                    <select
                      value={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.value })}
                      className="w-full h-12 px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1.5">Reference Doc File Name</label>
                    <input
                      value={formData.referenceDocFileName}
                      onChange={(e) => setFormData({ ...formData, referenceDocFileName: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. Training_Manual_v1.pdf"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1.5">Reference Doc File Location</label>
                    <input
                      value={formData.referenceDocFileLocation}
                      onChange={(e) => setFormData({ ...formData, referenceDocFileLocation: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                      placeholder="e.g. /documents/training/"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium"
                  >
                    {editingReference ? "Update Reference" : "Create Reference"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-8">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <a href="/about-us" className="hover:text-teal-300">About</a>
            <a href="/privacy-policy" className="hover:text-teal-300">Privacy Policy</a>
            <a href="/terms-of-use" className="hover:text-teal-300">Terms of Use</a>
            <a href="/contact-us" className="hover:text-teal-300">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProcessifydReferences;